import styled from 'styled-components';

export const GreeniaAIAgentModalStyles = styled.div`
    --greenia: #00a651;
    --greenia-dark: #00934a;
    --greenia-tint: #e9f9f0;

    position: fixed;
    inset: 0;
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(3px);

    * {
        box-sizing: border-box;
    }

    /* ---- Card shell ---- */
    .chat-card {
        width: 100%;
        max-width: 460px;
        height: min(720px, 86vh);
        background: #fff;
        border-radius: 20px;
        box-shadow: 0 24px 60px rgba(2, 32, 18, 0.28);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: greeniaPop 0.22s ease-out;
    }

    @keyframes greeniaPop {
        from { opacity: 0; transform: translateY(12px) scale(0.98); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    /* ---- Header ---- */
    .chat-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 16px;
        color: #fff;
        background: linear-gradient(135deg, #00b95c 0%, #009247 100%);
    }
    .chat-header .avatar {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.18);
        display: grid;
        place-items: center;
        font-size: 19px;
        flex-shrink: 0;
    }
    .chat-header .head-info {
        flex: 1;
        min-width: 0;
    }
    .chat-header .head-title {
        font-size: 16px;
        font-weight: 700;
        line-height: 1.2;
        margin: 0;
    }
    .chat-header .head-status {
        font-size: 12px;
        opacity: 0.92;
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 2px;
    }
    .chat-header .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #b8ffce;
        box-shadow: 0 0 0 3px rgba(184, 255, 206, 0.25);
    }
    .close-btn {
        width: 34px;
        height: 34px;
        border: 0;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.16);
        color: #fff;
        cursor: pointer;
        display: grid;
        place-items: center;
        font-size: 16px;
        transition: background 0.15s;
    }
    .close-btn:hover {
        background: rgba(255, 255, 255, 0.30);
    }

    /* ---- Body ---- */
    .chat-body {
        flex: 1;
        overflow-y: auto;
        padding: 18px 16px;
        background: #f6f8fa;
        display: flex;
        flex-direction: column;
        gap: 14px;
    }
    .chat-body::-webkit-scrollbar {
        width: 8px;
    }
    .chat-body::-webkit-scrollbar-thumb {
        background: #d3dae1;
        border-radius: 8px;
    }

    /* ---- Message rows ---- */
    .msg-row {
        display: flex;
        gap: 8px;
        align-items: flex-end;
        max-width: 88%;
    }
    .msg-row.ai {
        align-self: flex-start;
    }
    .msg-row.user {
        align-self: flex-end;
        flex-direction: row-reverse;
    }
    .msg-avatar {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        flex-shrink: 0;
        display: grid;
        place-items: center;
        font-size: 13px;
        color: #fff;
        background: linear-gradient(135deg, #00b95c, #009247);
    }
    .bubble {
        padding: 10px 13px;
        border-radius: 16px;
        font-size: 14px;
        line-height: 1.5;
        word-break: break-word;
    }
    .msg-row.ai .bubble {
        background: #fff;
        color: #1f2937;
        border: 1px solid #e7ebf0;
        border-bottom-left-radius: 4px;
    }
    .msg-row.user .bubble {
        background: var(--greenia);
        color: #fff;
        border-bottom-right-radius: 4px;
    }
    .bubble p:last-child {
        margin-bottom: 0;
    }
    .bubble a {
        color: inherit;
        text-decoration: underline;
    }
    .bubble .sender {
        font-size: 11px;
        font-weight: 700;
        color: var(--greenia-dark);
        margin-bottom: 3px;
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }

    /* Quote / info cards rendered under an AI message span the AI column */
    .ai-extra {
        align-self: flex-start;
        width: 100%;
        max-width: 92%;
    }

    /* ---- Typing indicator ---- */
    .typing {
        display: inline-flex;
        gap: 4px;
        align-items: center;
        padding: 3px 2px;
    }
    .typing span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #9aa5b1;
        animation: greeniaBounce 1.2s infinite ease-in-out;
    }
    .typing span:nth-child(2) { animation-delay: 0.15s; }
    .typing span:nth-child(3) { animation-delay: 0.30s; }
    @keyframes greeniaBounce {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
        30% { transform: translateY(-5px); opacity: 1; }
    }

    /* ---- Empty state ---- */
    .empty-state {
        margin: auto;
        text-align: center;
        padding: 12px;
        max-width: 340px;
    }
    .empty-state .big-avatar {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        margin: 0 auto 14px;
        display: grid;
        place-items: center;
        font-size: 29px;
        color: #fff;
        background: linear-gradient(135deg, #00b95c, #009247);
        box-shadow: 0 10px 24px rgba(0, 166, 81, 0.35);
    }
    .empty-state h3 {
        font-size: 18px;
        font-weight: 700;
        margin: 0 0 6px;
        color: #111827;
    }
    .empty-state p {
        font-size: 13px;
        color: #6b7280;
        margin: 0 0 16px;
        line-height: 1.5;
    }
    .suggested {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .chip {
        border: 1px solid #d7ece0;
        background: #fff;
        color: #0b7a3f;
        padding: 10px 14px;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        text-align: left;
        transition: background 0.15s, border-color 0.15s, transform 0.1s;
        display: flex;
        align-items: center;
        gap: 9px;
    }
    .chip:hover {
        background: var(--greenia-tint);
        border-color: var(--greenia);
        transform: translateY(-1px);
    }
    .chip .chip-ico {
        width: 15px;
        color: var(--greenia);
        flex-shrink: 0;
    }

    /* ---- Input ---- */
    .chat-input {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        padding: 12px 14px;
        border-top: 1px solid #eceff2;
        background: #fff;
    }
    .chat-input textarea {
        flex: 1;
        resize: none;
        border: 1px solid #dde3e9;
        border-radius: 22px;
        padding: 11px 16px;
        font-size: 14px;
        line-height: 1.4;
        max-height: 120px;
        outline: none;
        font-family: inherit;
        transition: border-color 0.15s, box-shadow 0.15s;
    }
    .chat-input textarea:focus {
        border-color: var(--greenia);
        box-shadow: 0 0 0 3px rgba(0, 166, 81, 0.12);
    }
    .chat-input textarea:disabled {
        background: #f3f5f7;
    }
    .send-btn {
        width: 44px;
        height: 44px;
        flex-shrink: 0;
        border: 0;
        border-radius: 50%;
        background: var(--greenia);
        color: #fff;
        cursor: pointer;
        display: grid;
        place-items: center;
        font-size: 16px;
        transition: background 0.15s, transform 0.1s;
    }
    .send-btn:hover:not(:disabled) { background: var(--greenia-dark); }
    .send-btn:active:not(:disabled) { transform: scale(0.94); }
    .send-btn:disabled {
        background: #bcc6cf;
        cursor: not-allowed;
    }

    /* ---- Mobile: full screen ---- */
    @media (max-width: 576px) {
        padding: 0;

        .chat-card {
            max-width: 100%;
            height: 100%;
            border-radius: 0;
        }
    }
`;
